import { addSubscribe } from '@flowus/common';
import { BuildinSubscribePay } from '@flowus/common/components';
import { LogoSlogan } from '@flowus/common/components/logo-slogan';
import { useOpenPayAccessFeeCode } from '@flowus/common/components/pay-access-fee-code';
import { cx } from '@flowus/common/cx';
import { useApiState } from '@flowus/common/hooks/use-api-state';
import { useOpenModal } from '@flowus/common/next-modal';
import { getSessionOp } from '@flowus/common/storage';
import { PayType } from '@flowus/common/utils/pay-info';
import { useSystemIsRu } from '@flowus/common/utils/system';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import { Avatar } from 'src/common/components/avatar';
import { Button } from 'src/common/components/button';
import { Icon } from 'src/common/components/icon';
import { UNTITLED } from 'src/common/const';
import { request } from 'src/common/request';
import { Placeholder } from 'src/components/empty/placeholder';
import { HEADER_HEIGHT } from 'src/const/public';
import { PRODUCT_NAME } from 'src/const/title';
import { isBuildIn } from 'src/env';
import { useBlock } from 'src/hooks/block/use-block';
import { useFetchPage } from 'src/hooks/page';
import { usePayInfo } from 'src/hooks/page/use-pay-info';
import { getCurrentUser } from 'src/hooks/user';
import { useIsLogin } from 'src/hooks/user/use-is-login';
import { Modals } from 'src/modals';
import { cache } from 'src/redux/store';
import { bizTracker } from 'src/utils/biz-tracker';
import { findFirstAllowSubscribePage } from 'src/utils/block-utils';
import { getCurrencySymbols } from 'src/utils/currency-format';
import { useGetPageId } from 'src/utils/getPageId';
import {
  MESSAGE_CODE_HIDE_SUBSCRIBE_CARD,
  MESSAGE_CODE_SHOW_SUBSCRIBE_CARD,
} from 'src/utils/message-code';
import { ShareRebateUrlButton } from 'src/views/main/header/subscribe-button';
import { Page } from 'src/views/main/page';
import { PageScene, usePageScene } from 'src/views/main/scene-context';
import { useCheckOrder } from 'src/views/upgrade/h5/use-check-order';
import { PayQrModal } from '../select-upgrade-plan/pay-qr-modal';
import { computePeriod } from '../share/share-pay';
import { useOpenBuildInPay } from '../stripe';
import { useOpenLogin } from './open-login';
import { PayAccessFeeContext } from './pay-access-fee-context';
import type { PreOrderDTO, SpaceDTO } from '@next-space/fe-api-idl';
import { useIsProSpace } from 'src/hooks/space/use-is-pro-space';
import { message } from '@flowus/common/components/message';
import { updateSpaces } from 'src/services/spaces/spaces';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';

interface Props {}
export const PayAccessFee: FC<Props> = () => {
  const pageId = useGetPageId();
  const block = useBlock(pageId);
  const { payInfo } = usePayInfo(pageId);

  if (payInfo?.openPreview && block) {
    return (
      <PayAccessFeeContext.Provider value={payInfo.spaceId}>
        <div className="w-full relative main-panel h-full min-h-0 min-w-0 flex-1 gt-xl:min-w-[640px]">
          <Page uuid={pageId}>
            <PayAccessFeeContent payInfo={payInfo} uuid={pageId} className="border-t" />
          </Page>
        </div>
      </PayAccessFeeContext.Provider>
    );
  }

  return <PayAccessFeeContent payInfo={payInfo} uuid={pageId} />;
};

interface PayTipProps {
  uuid: string;
  className?: string;
  payInfo: ReturnType<typeof usePayInfo>['payInfo'];
}
export const PayAccessFeeContent: FC<PayTipProps> = (props) => {
  const { t } = useAppTranslation();
  const { uuid, className, payInfo } = props;
  const shareUserCode = getSessionOp.inviteCode.get();
  const [shareUserInfo] = useApiState({
    getApi: () => request.infra.getUserByCode(shareUserCode),
    enable: !!shareUserCode,
  });
  const pageScene = usePageScene();
  const isInTemplateCenter = pageScene === PageScene.TEMPLATE;
  useEffect(() => {
    if (!payInfo?.spaceId) return;
    const loadSpaceInfo = async () => {
      const res = await request.infra.getSpacesPublicData({
        spaceIds: [payInfo.spaceId],
      });
      updateSpaces(res.spaces as Record<string, SpaceDTO>);
    };
    void loadSpaceInfo();
  }, [payInfo?.spaceId]);

  if (!payInfo) return null;

  const payContent = (
    <div className="pb-10 px-10 flex items-center justify-between text-t2 flex-col max-w-lg">
      <div>
        <div className="flex flex-col items-center space-y-2.5">
          {payInfo && (
            <>
              <div className="text-h4">{payInfo.publishAccessPageTitle ?? UNTITLED}</div>
            </>
          )}
          <div className="flex flex-col space-y-2">
            <span className="flex text-t2 space-x-2">
              <Avatar
                className="flex-shrink-0 w-[26px] h-[26px]"
                icon={payInfo.spaceIcon}
                name={payInfo.spaceTitle}
                color={payInfo.spaceBackgroundColor}
              />
              <span className="ml-0.5 text-t2-medium text-ellipsis">{payInfo.spaceTitle}</span>
            </span>
            {shareUserInfo && (
              <span className="space-x-1 text-t2 text-center">
                <span className="text-t4 text-grey3">{t('pay_access.recommender')}</span>
                <span className="text-t4">{shareUserInfo?.nickname}</span>
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="mt-5 w-full text-center text-t2">{t('pay_access.payment_notice')}</div>
      <div className="text-t4 text-grey3 space-y-1 mb-10 mt-1">
        <div>{t('pay_access.notice_1')}</div>
        <div>{t('pay_access.notice_2')}</div>
      </div>
      <LogoSlogan />
    </div>
  );

  return (
    <Placeholder className={cx('!pt-24', className)}>
      <div
        className="fixed bg-white1 top-0 left-0 w-full flex items-center justify-end px-5 space-x-2.5 z-50"
        style={{ height: HEADER_HEIGHT, top: isInTemplateCenter ? '55px' : '0px' }}
      >
        <ShareRebateUrlButton />
      </div>
      {payContent}
      {/* <Footer /> */}
      <PayCard payInfo={payInfo} uuid={uuid} />
    </Placeholder>
  );
};

const PayCard: FC<PayTipProps> = (props) => {
  const { t } = useAppTranslation();
  const isLogin = useIsLogin();
  const { payInfo, uuid } = props;
  const openLogin = useOpenLogin();
  const openPayAccessFeeCode = useOpenPayAccessFeeCode({
    isLogin,
    openLogin,
  });
  const shareUserCode = getSessionOp.inviteCode.get();
  const fetchPage = useFetchPage();
  const openModal = useOpenModal();
  const isTemplate = usePageScene() === PageScene.TEMPLATE;
  const [orderId, setOrderId] = useState<string>();
  const isSystemIsRu = useSystemIsRu();
  // 只有buildin里面的逻辑才会执行，flowus显示的是二维码，不需要检查订单状态
  useCheckOrder(
    orderId,
    () => {
      void fetchPage(uuid);
    },
    false
  );
  const isPro = useIsProSpace(payInfo?.spaceId);

  const onFinsh = useCallback(
    (order?: PreOrderDTO) => {
      if (!payInfo) return;
      bizTracker.event('pay_access_fee', {
        spaceId: payInfo.spaceId,
        duration: `${payInfo.accessFeeValidDays || 'forever'}`,
        amount: order?.amount || 0,
      });
      const targetId = findFirstAllowSubscribePage(payInfo.accessPageId) ?? payInfo.accessPageId;
      setTimeout(async () => {
        void fetchPage(uuid);
        const createBy = cache.blocks[payInfo.accessPageId]?.createdBy;
        await addSubscribe(
          {
            targetId,
            subscribed: true,
            addSubscribe: request.infra.addSubscribe,
            cancelSubscribe: request.infra.cancelSubscribe,
            user: payInfo?.ownerUser,
            openDialog: openModal.warning,
            callback: (status) => {
              if (!isTemplate) {
                return;
              }
              status && location.reload();
            },
          },
          {
            pageId: payInfo.accessPageId,
            needPay: true,
            userId: getCurrentUser().uuid,
            createdBy: createBy,
            spaceId: payInfo.spaceId,
            event: bizTracker.event,
          }
        );
      }, 1000);
    },
    [payInfo, fetchPage, uuid, openModal, isTemplate]
  );

  const openBuildPay = useOpenBuildInPay();
  const payButton = useMemo(() => {
    if (!payInfo) return;
    const getPaidOrder = async (payType?: PayType) => {
      const ret = await request.infra.createSharePaidOrder({
        payType: __BUILD_IN__ ? payType ?? PayType.webpay : PayType.wxpay,
        spaceId: payInfo.spaceId,
        sharePageId: payInfo.accessPageId,
        distributorCode: shareUserCode,
      });
      return ret;
    };
    const clickPay = async () => {
      if (!isPro && !isBuildIn()) {
        message.warning(t('pay_access.buy_failed'));
        return;
      }
      if (!isLogin) {
        openLogin();
        return;
      }
      window.parent.postMessage(
        {
          code: MESSAGE_CODE_HIDE_SUBSCRIBE_CARD,
        },
        '*'
      );

      if (isBuildIn()) {
        openModal.modal({
          closeAfterCallBack: () => {
            window.parent.postMessage(
              {
                code: MESSAGE_CODE_SHOW_SUBSCRIBE_CARD,
              },
              '*'
            );
          },
          content: ({ onCloseModal }) => {
            return (
              <BuildinSubscribePay
                className="min-w-[320px]"
                showRuPay={isSystemIsRu}
                onPayClick={async (_payType) => {
                  const order = await getPaidOrder(_payType);
                  setOrderId(order.orderId);
                  openBuildPay(order);
                  onCloseModal();
                }}
              />
            );
          },
        });
        return;
      }

      const order = await getPaidOrder();

      openModal.modal({
        modalId: Modals.PAY_MODAL,
        closeAfterCallBack: () => {
          window.parent.postMessage(
            {
              code: MESSAGE_CODE_SHOW_SUBSCRIBE_CARD,
            },
            '*'
          );
        },
        content: () => (
          <PayQrModal
            rePayOrder={getPaidOrder}
            onFinish={async () => {
              onFinsh(order);
            }}
            payType={__BUILD_IN__ ? PayType.webpay : PayType.wxpay}
            // 后期海外版需要amountUnit这个字段
            priceTitle={`${getCurrencySymbols(undefined)}${order.amount?.toFixed(2)}`}
            orderId={`${order.orderId}`}
            qrCodeUrl={`${order.qrCodeUrl}`}
          />
        ),
      });
    };

    return (
      <Button
        size="large"
        className="w-full h-auto min-h-[40px] py-2 flex-col gap-1 items-center justify-center p-2"
        colorType="active"
        onClick={clickPay}
      >
        <span className="text-t2-medium whitespace-normal text-center leading-tight">
          {t('pay_access.buy_content')}
        </span>
        <div className="flex items-center space-x-1">
          <span className="text-h4 font-medium whitespace-nowrap">
            {`${getCurrencySymbols(undefined)}${payInfo?.accessFee.toFixed(2)}`}
          </span>
          {payInfo.publicOriginPrice !== undefined && payInfo.publicOriginPrice > 0 && (
            <span className="line-through text-t2 text-white/70 whitespace-nowrap">
              {[t('common.original_price'), getCurrencySymbols(undefined)].join(' ')}
              <span>{payInfo.publicOriginPrice}</span>
            </span>
          )}
        </div>
      </Button>
    );
  }, [
    payInfo,
    shareUserCode,
    isPro,
    isLogin,
    openModal,
    openLogin,
    isSystemIsRu,
    openBuildPay,
    onFinsh,
    t,
  ]);

  return (
    <div
      className={cx('w-full flex justify-center z-[60]', {
        'fixed bottom-8 mt-8': payInfo?.openPreview,
      })}
    >
      <div
        className={cx('w-[452px] p-4', {
          'next-modal': payInfo?.openPreview,
        })}
      >
        <>{payButton}</>
        <>
          <div className="mt-4 gap-2 text-t2 w-full flex flex-wrap items-center justify-center text-grey3">
            <div className="flex items-center space-x-2">
              <Icon name={'IcHistory'} size="middle" />
              <div>
                {[
                  t('common.validity_period'),
                  [isBuildIn() ? ': ' : ':'],
                  computePeriod(payInfo?.accessFeeValidDays),
                ].join('')}
              </div>
            </div>
            <div
              className="animate-click text-active_color whitespace-nowrap"
              onClick={() => {
                if (!isPro && !isBuildIn()) {
                  message.warning(t('pay_access.redeem_failed'));
                  return;
                }
                openPayAccessFeeCode(onFinsh);
              }}
            >
              {t('pay_access.use_redeem_code')}
            </div>
          </div>
        </>
      </div>
    </div>
  );
};

// const Footer: FC = () => {
//   const report = useReportIllegal();
//   const isLogin = useIsLogin();
//   const openLogin = useOpenLogin();
//   return (
//     <div className="w-full bg-white1 border-t">
//       <div className="mt-8 text-t2 flex items-center space-x-9 justify-center">
//         <a className="animate-click" href={publicLink.learnMore} target="_blank">
//           了解 FlowUs
//         </a>
//         <a className="animate-click" href={publicLink.shareDataPanel} target="_blank">
//           问题反馈
//         </a>
//         <a className="animate-click" href={publicLink.subscribeRule} target="_blank">
//           使用帮助
//         </a>
//         <a className="animate-click" onClick={report}>
//           举报违法信息
//         </a>
//         {isLogin && (
//           <a className="animate-click" onClick={() => openLogin()}>
//             快速注册 FlowUs
//           </a>
//         )}
//       </div>
//     </div>
//   );
// };
